import { useEffect, useState } from "react";
import { withTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useCurrentUser } from "@hooks/use-current-user";
import SharedAccountsLayout from "@components/core/SharedAccountsLayout";
import Button from "@components/core/Button";
import Input from "@components/core/Input";
import Textarea from "@components/core/Textarea";
import IconSvg from "@components/core/IconSvg";
import { isStringNotEmpty, isEmailValid } from "@utils/validations";
import { postTicket } from "@utils/call-api/help-call";
import { showSuccessToast, showErrorToast } from "@utils/toasts/showToasts";

function ContactUs({ t }) {
  const user = useCurrentUser();
  const router = useRouter();

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({
    email: null,
    message: null,
    files: null,
  });

  function addFile(e) {
    setFiles([...files, ...e.target.files]);
  }

  function removeFile(fileToRemoveIndex) {
    setFiles(files.filter((file, i) => i !== fileToRemoveIndex));
  }

  function isDataValid() {
    const newErrors = {};

    if (!isStringNotEmpty(email)) {
      newErrors.email = t("help:contact_us.errors.email_required");
    } else if (!isEmailValid(email)) {
      newErrors.email = t("help:contact_us.errors.invalid_email");
    }

    if (!isStringNotEmpty(message)) {
      newErrors.message = t("help:contact_us.errors.message_required");
    }

    if (files.reduce((accumulator, file) => accumulator + file.size, 0) > 50 * 1024 * 1024) {
      newErrors.files = t("help:contact_us.errors.files_too_large");
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  }

  async function sendMessage() {
    if (loading) return;
    if (!isDataValid()) return;

    setLoading(true);

    try {

      await postTicket(email, message, files);
      router.push("/help");
      showSuccessToast(t("help:contact_us.success.title"), t("help:contact_us.success.message"));
    } catch (err) {
      showErrorToast(t("help:contact_us.send_error.title"), t("help:contact_us.send_error.message"));
    } finally {
      setLoading(false);
    }
  }

  function displayFiles() {
    return files.map((file, index) => (
      <div key={index}>
        <div
          key={index}
          className="contact-us-file-description"
        >
          <span className="file-img-container">
            <IconSvg name="paperclip" className="w-100" />
          </span>

          <span className="file-name">
            {file.name}
          </span>

          <span className="font-size-xs me-3 text-nowrap">
            {t("help:contact_us.file_size", { fileSize: (file.size / (1024*1024)).toFixed(1) })}
          </span>

          <Button
            size="sm"
            variant="link-dark"
            onClick={() => removeFile(index)}
            leftIconName="close"
          />
        </div>
      </div>
    ));
  }

  useEffect(() => {
    if (user) {
      setEmail(user.email);
    }
  }, [user?.email]);

  return (
    <div>
      <SharedAccountsLayout title="help_center" />

      <div className="container support-container py-4 my-2 py-md-6 my-md-0">
        <div className="mb-6">
          <h2 className="h3 mb-5 text-center">
            {t("help:contact_us.title")}
          </h2>

          <Input
            name="email"
            label={t("help:contact_us.email")}
            type="email"
            value={email}
            error={errors.email}
            onChange={(e) => setEmail(e.target.value)}
            className="mb-3"
            autoComplete="email"
          />

          <Textarea
            className="mb-3"
            placeholder={t("help:contact_us.msg_placeholder")}
            value={message}
            onChange={(e) => setMessage(e.currentTarget.value)}
            rows="5"
            error={errors.message}
          />

          <div>
            <div className="contact-us-files-zone-container">
              <div className="contact-us-files-zone">
                <span className="text-center text-gray mb-2">{t("help:contact_us.add_files")}</span>
                <div className="folder-img-container">
                  <IconSvg name="folder" />
                </div>
              </div>

              <input
                className="input-files"
                type="file"
                multiple
                id="profile_pic"
                name="profile_pic"
                onChange={(e) => addFile(e)}
              />
            </div>

            {errors.files && (
              <p className="text-danger font-size-xs mb-2">
                {errors.files}
              </p>
            )}

            <div className="d-flex flex-column">
              {displayFiles()}
            </div>
          </div>
        </div>

        <div className="d-flex w-100 justify-content-center">
          <Button
            variant="primary"
            loading={loading}
            onClick={sendMessage}
          >
            {t("help:contact_us.send_msg")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default withTranslation("help")(ContactUs);
